import React, { useEffect, useState } from 'react';
import { FlexColumnWidth100, FlexRow } from '../../../../components/ui/Content';
import { useTranslation } from 'react-i18next';
import { addError, cleanError, SendPhoneCode, setPhone, setPhoneSubmit } from '../../slice';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { RegisterInputContainer } from '../../../../components/ui/InputSearch';
import { InputPhone } from '../../../../ui/Input/InputPhone';
import { Caption2, Caption2PointerLink } from '../../../../components/Font';
import { useTheme } from 'styled-components';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import ru from 'react-phone-number-input/locale/ru.json';
import en from "react-phone-number-input/locale/en.json";
import PhoneConfirmModal from './Modals/PhoneConfirmModal';
import {
  cleanError as cleanPhoneError,
  ConfirmPhoneByFlashCall,
  ConfirmPhoneBySms,
  ConfirmPhoneByUserCall,
  resetPhoneConfirmation,
  setPhoneConfirmed,
  setSelectedPhoneConfirmationWay,
} from './slice';
import { PhoneConfirmationWay, MinPhoneLength } from '../../../../enum/phoneConfirm';
import { ArrowRightBlue, Info, SuccessIcon } from '../../../../icon/Icon';
import { IRegisterForm } from '../../../../interface/Auth/frontend';
import { PassportType } from '../RegisterForm/functions/checkRegFormFunctions';
interface IProps {
  isDisabled?: boolean;
  registerForm?: IRegisterForm;
}

export const EnterPhone: React.FC<IProps> = ({ isDisabled, registerForm }) => {
  const Theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    error,
    isFetching,
    phone: savedPhone,
    registerInitData,
    phoneSubmit: phoneConfirmed,
  } = useAppSelector(state => state.auth);
  const [localPhone, setLocalPhone] = useState(savedPhone ?? '');
  const { ways, error: phoneConfirmError } = useAppSelector(state => state.phoneConfirm);
  const needPhoneVerification = useAppSelector(state => state.auth.needPhoneVerification);

  const [phoneError, setPhoneError] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [firstConfirmSent, setFirstConfirmSent] = useState(false);
  const [editPhone, setEditPhone] = useState(false);
  const [phoneLang] = useState(() => {
    const lang = localStorage.getItem("lang");
    return lang === "ru" ? ru : en;
  });

  useEffect(() => {
    if (savedPhone === null) {
      setLocalPhone('');
    } else if (savedPhone !== localPhone) {
      setLocalPhone(savedPhone);
    }
  }, [savedPhone]);

  useEffect(() => {
    if (registerForm?.citizenshipId) {
      setLocalPhone('');
      dispatch(setPhone(''));
      dispatch(setPhoneSubmit(false));
    }
  }, [registerForm?.citizenshipId]);

  useEffect(() => {
    const input = document.getElementById('registerPhoneInput') as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }, []);

  useEffect(() => {
    if (!firstConfirmSent && confirmModalOpened && ways.find(i => i.id === PhoneConfirmationWay.userCall)) {
      setFirstConfirmSent(true);
      dispatch(
          ConfirmPhoneByUserCall({
            phone_number: localPhone,
            confirmation_type_id: PhoneConfirmationWay.userCall,
          })
      );
      dispatch(setSelectedPhoneConfirmationWay(PhoneConfirmationWay.userCall));
    }
  }, [ways, confirmModalOpened]);

  useEffect(() => {
    if (registerInitData && registerInitData.phone) {
      setLocalPhone(registerInitData.phone);
    }
  }, [registerInitData]);

  useEffect(() => {
    setEditPhone(false);
    if (phoneConfirmed) {
      dispatch(setPhoneConfirmed(true));
    }
  }, [phoneConfirmed]);

  useEffect(() => {
    if (phoneConfirmError['phone_number']) {
      setConfirmModalOpened(false);
      dispatch(addError({ key: 'phone', value: phoneConfirmError['phone_number'] }));
      dispatch(cleanPhoneError('phone_number'));
    }
  }, [phoneConfirmError]);

const checkForRuLocale = (phone: string) => {
    // Удаляем все нецифровые символы (кроме плюса в начале)
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Российские коды регионов (первые цифры после +7/8)
  const russianRegionCodes = ['9', '8', '4'];

  // Актуальные казахстанские коды (по данным 2024)
  const kazakhCodes = ['77',];

  // Проверка базового формата
  if (!/^(\+7|8|7)\d{10}$/.test(cleaned)) {
    return false;
  }

  // Извлекаем цифры после кода страны
  const digits = cleaned.startsWith('+7') ? cleaned.substring(2) :
                 cleaned.startsWith('8') ? cleaned.substring(1) :
                 cleaned.substring(1);

  // Проверка на казахстанские номера
  if (cleaned.startsWith('+7')) {
    const prefix = digits.substring(0, 2);
    if (kazakhCodes.includes(prefix)) {
      return false;
    }
  }

  // Проверка российского кода региона
  return russianRegionCodes.includes(digits[0]);
  };

  const phoneSubmit = () => {
    if (phoneError) {
      dispatch(addError({ key: 'phone', value: t('auth.enterValidPhone') }));
    } else {
      if (!checkForRuLocale(localPhone) && registerForm?.citizenshipId === PassportType.Foreign) {
        // console.log('Без верификации');

        if (isValidPhoneNumber(localPhone)) {
          dispatch(
            SendPhoneCode({
              phone: localPhone.replace(/^[ ()+-]+$/, ''),
              needPhoneVerification,
            })
          );
          dispatch(setPhoneSubmit(true));
        } else {
          dispatch(addError({ key: 'phone', value: t('auth.enterValidPhone') }));
        }

        return;
      }

      if (savedPhone === localPhone) {
        setEditPhone(false);
      } else {
        if (needPhoneVerification) {
          setFirstConfirmSent(false);
          dispatch(resetPhoneConfirmation());
          setConfirmModalOpened(true);
        } else {
          dispatch(
            SendPhoneCode({
              phone: localPhone.replace(/^[ ()+-]+$/, ''),
              needPhoneVerification,
            })
          );
        }
      }
    }
  };

  const onWaySubmit = (selectedWay: PhoneConfirmationWay) => {
    switch (selectedWay) {
      case PhoneConfirmationWay.sms: {
        dispatch(
          ConfirmPhoneBySms({
            phone_number: localPhone,
            confirmation_type_id: PhoneConfirmationWay.sms,
          })
        );
        break;
      }
      case PhoneConfirmationWay.flashCall: {
        dispatch(
          ConfirmPhoneByFlashCall({
            phone_number: localPhone,
            confirmation_type_id: PhoneConfirmationWay.flashCall,
          })
        );
        break;
      }
      case PhoneConfirmationWay.userCall: {
        dispatch(
          ConfirmPhoneByUserCall({
            phone_number: localPhone,
            confirmation_type_id: PhoneConfirmationWay.userCall,
          })
        );
        break;
      }
      // NOTE: не трогай это на новый год
      // case PhoneConfirmationWay.push: {
      //     dispatch(ConfirmPhoneByPush({token: 'idk'}))
      //     break;
      // }
      // case PhoneConfirmationWay.email: {
      //     dispatch(ConfirmPhoneByEmail({email: 'idk'}))
      //     break;
      // }
      default:
        break;
    }
  };

  return (
    <FlexColumnWidth100>
      <RegisterInputContainer>
        <PhoneInput
          id={'registerPhoneInput'}
          className={'PhoneInput'}
          countrySelectComponent={InputPhone}
          countrySelectProps={{ unicodeFlags: true }}
          error={error['phone']}
          disabled={isDisabled}
          international
          countryCallingCodeEditable={false}
          limitMaxLength={true}
          labels={phoneLang}
          defaultCountry='RU'
          value={localPhone}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.code === 'Enter' || e.code === 'NumpadEnter') {
              phoneSubmit();
            }
          }}
          onChange={value => {
            if (value) {
              if (error['phone']) {
                dispatch(cleanError('phone'));
              }
              dispatch(setPhoneSubmit(false));
              setPhoneError(!isValidPhoneNumber(value));
              setLocalPhone(value.toString());
            }
          }}
        />
        {localPhone && !(phoneConfirmed && localPhone === savedPhone) && (
          <Info
            style={{
              fill: error['phone'] ? Theme.color.danger : Theme.color.warning,
              position: 'absolute',
              top: 'calc(50% - 12px)',
              right: 15,
            }}
          />
        )}
        {phoneConfirmed && localPhone === savedPhone && (
          <SuccessIcon
            style={{
              fill: error['phone'] ? Theme.color.danger : Theme.color.warning,
              position: 'absolute',
              top: 'calc(50% - 12px)',
              right: 15,
            }}
          />
        )}
      </RegisterInputContainer>

      {error['phone'] && (
        <Caption2 style={{ marginTop: '4px', textAlign: 'start', width: '100%' }} color={Theme.color.danger}>
          {error['phone']}
        </Caption2>
      )}

      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        {!isFetching && !error['phone'] && localPhone.length > MinPhoneLength && localPhone !== savedPhone && (
          <Caption2PointerLink style={{ marginTop: '10px' }} onClick={phoneSubmit}>
            <FlexRow
              style={{
                fontSize: '16px',
                lineHeight: '22px',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {t('passport.registrationForm.phoneConfirm.name')}
              <ArrowRightBlue width='15' height='15' />
            </FlexRow>
          </Caption2PointerLink>
        )}
        {editPhone && (
          <Caption2PointerLink style={{ marginRight: '10px' }} onClick={() => setEditPhone(false)}>
            {t('auth.cancel')}
          </Caption2PointerLink>
        )}
      </div>
      {/* Модалка подтверждения */}
      {confirmModalOpened && <PhoneConfirmModal onSubmit={onWaySubmit} onClose={() => setConfirmModalOpened(false)} />}
    </FlexColumnWidth100>
  );
};
